import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { Camera, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function QrCameraScanner({ onScan, onClose }) {
    const [error, setError] = useState(null);
    const scannerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        let html5QrCode = null;

        const startScanner = async () => {
            try {
                html5QrCode = new Html5Qrcode("qr-reader");
                scannerRef.current = html5QrCode;

                if (!mountedRef.current) return;

                await html5QrCode.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
                    (decodedText) => {
                        // Stop scanner safely before calling onScan
                        try {
                            const state = html5QrCode.getState();
                            if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                                html5QrCode.stop().catch(() => {});
                            }
                        } catch (e) { /* ignore */ }
                        onScan(decodedText);
                    },
                    () => {}
                );
            } catch (err) {
                if (!mountedRef.current) return;
                console.error('[QR Scanner] Error:', err);
                const msg = String(err);
                if (msg.includes('Permission')) {
                    setError("Accès caméra refusé. Autorisez l'accès dans les paramètres du navigateur.");
                } else if (msg.includes('NotFound') || msg.includes('Requested device not found')) {
                    setError("Aucune caméra trouvée sur cet appareil.");
                } else {
                    setError("Impossible de démarrer la caméra. Vérifiez les permissions.");
                }
            }
        };

        startScanner();

        return () => {
            mountedRef.current = false;
            if (scannerRef.current) {
                try {
                    const state = scannerRef.current.getState();
                    if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                        scannerRef.current.stop().then(() => {
                            scannerRef.current.clear();
                        }).catch(() => {});
                    }
                } catch (e) { /* scanner not started yet, ignore */ }
            }
        };
    }, [onScan]);

    const handleClose = () => {
        if (scannerRef.current) {
            try {
                const state = scannerRef.current.getState();
                if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                    scannerRef.current.stop().catch(() => {});
                }
            } catch (e) { /* ignore */ }
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center" data-testid="qr-camera-overlay">
            <div className="relative w-full max-w-md mx-auto">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2 text-white">
                        <Camera className="w-5 h-5" />
                        <span className="text-sm font-medium">Scanner le QR code du client</span>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClose}
                        className="text-white hover:bg-white/20 rounded-full"
                        data-testid="close-camera-btn"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <div className="relative mx-4 rounded-2xl overflow-hidden bg-black">
                    <div id="qr-reader" className="w-full" style={{ minHeight: 300 }} />
                </div>

                {error && (
                    <div className="mx-4 mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-center">
                        <p className="text-red-200 text-sm">{error}</p>
                    </div>
                )}

                <p className="text-white/50 text-xs text-center mt-4 px-4">
                    Pointez la caméra vers le QR code affiché sur l'écran du client
                </p>
            </div>
        </div>
    );
}
